import React, { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { F, D } from "../../styles/palette";
import {
  getDeviceId,
  getDeviceInfo,
  isDeviceCertified,
  canAddDevice,
  registerDevice,
  updateLastLogin,
} from "../../utils/deviceManager";
import { verifyTOTPToken } from "../../utils/totpManager";
import { logAudit } from "../../utils/auditTrail";

export default function LoginScreen({ onLogin, onGoToRegister, onRegisterPatient, onRegisterDoctor, onGoToTOTPSetup }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showTOTP, setShowTOTP] = useState(false);
  const [totpSecret, setTotpSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loggedUser, setLoggedUser] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // Lockout countdown timer
  useEffect(() => {
    if (!lockoutUntil) {
      setLockoutSeconds(0);
      return;
    }
    const tick = () => {
      const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setLockoutSeconds(0);
        setFailedAttempts(0);
      } else {
        setLockoutSeconds(remaining);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const isLockedOut = lockoutUntil && Date.now() < lockoutUntil;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    // Check lockout
    if (isLockedOut) {
      setError(`Cuenta bloqueada temporalmente. Intente en ${lockoutSeconds} segundos.`);
      return;
    }

    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      // Cargar perfil desde Firestore
      const profileRef = doc(db, "users", user.uid);
      const profileSnap = await getDoc(profileRef);

      if (!profileSnap.exists()) {
        setError("Perfil de usuario no encontrado.");
        setLoading(false);
        return;
      }

      const profile = profileSnap.data();

      // Check suspended account
      if (profile.suspended === true) {
        setError("Su cuenta ha sido suspendida. Contacte al administrador.");
        setLoading(false);
        return;
      }

      // --- Flujo para DOCTOR ---
      if (profile.role === "doctor") {
        // Verificar aprobacion
        if (!profile.approved) {
          setError("Cuenta pendiente de aprobacion. Un administrador revisara su solicitud.");
          setLoading(false);
          return;
        }

        // Verificar email
        if (!user.emailVerified) {
          await sendEmailVerification(user);
          setInfo("Se ha enviado un correo de verificacion. Por favor revise su bandeja de entrada.");
          setLoading(false);
          return;
        }

        // Verificar TOTP
        if (profile.totpEnabled) {
          // Si no se ha mostrado el campo TOTP aun, mostrarlo
          if (!showTOTP) {
            setShowTOTP(true);
            setTotpSecret(profile.totpSecret || "");
            setLoggedUser(user);
            setLoading(false);
            return;
          }
          // Si ya se muestra, esperar a que se envie el codigo
        } else {
          // TOTP no configurado, redirigir a setup
          if (onGoToTOTPSetup) {
            onGoToTOTPSetup(user, profile);
          }
          setLoading(false);
          return;
        }
      }

      // --- Flujo para PACIENTE ---
      if (profile.role === "patient") {
        if (!user.emailVerified) {
          await sendEmailVerification(user);
          setInfo("Se ha enviado un correo de verificacion. Por favor revise su bandeja de entrada.");
          setLoading(false);
          return;
        }
      }

      // Verificar dispositivo (solo doctores)
      if (profile.role === "doctor") {
        const deviceId = getDeviceId();
        const certified = await isDeviceCertified(user.uid, deviceId);

        if (!certified) {
          const canAdd = await canAddDevice(user.uid);
          if (!canAdd) {
            setError(
              "Limite de dispositivos alcanzado (maximo 3). Elimine un dispositivo desde su perfil para continuar."
            );
            setLoading(false);
            return;
          }
          const deviceInfo = getDeviceInfo();
          await registerDevice(user.uid, deviceId, deviceInfo);
        } else {
          await updateLastLogin(user.uid, deviceId);
        }
      }

      // Login exitoso
      setFailedAttempts(0);
      await logAudit("login", "user", user.uid, user.uid, email, "Login exitoso");
      if (onLogin) onLogin(user, profile);
    } catch (err) {
      // Audit failed login attempt
      await logAudit("login_fallido", "user", "", "", email, "Intento fallido");

      // Brute-force protection
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= 5) {
        setLockoutUntil(Date.now() + 60000);
      }

      // Generic error messages to prevent email enumeration
      const genericCredentialMsg = "Credenciales invalidas. Verifique su correo y contrasena.";
      const msgs = {
        "auth/user-not-found": genericCredentialMsg,
        "auth/wrong-password": genericCredentialMsg,
        "auth/invalid-credential": genericCredentialMsg,
        "auth/invalid-email": "Correo electronico invalido.",
        "auth/too-many-requests": "Demasiados intentos. Intente mas tarde.",
      };
      setError(msgs[err.code] || `Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!totpCode || totpCode.length !== 6) {
      setError("Ingrese un codigo de 6 digitos.");
      setLoading(false);
      return;
    }

    const valid = verifyTOTPToken(totpSecret, totpCode);
    if (!valid) {
      setError("Codigo TOTP invalido. Intente nuevamente.");
      setLoading(false);
      return;
    }

    // Verificar dispositivo
    const deviceId = getDeviceId();
    const certified = await isDeviceCertified(loggedUser.uid, deviceId);

    if (!certified) {
      const canAdd = await canAddDevice(loggedUser.uid);
      if (!canAdd) {
        setError(
          "Limite de dispositivos alcanzado (maximo 3). Elimine un dispositivo desde su perfil."
        );
        setLoading(false);
        return;
      }
      const deviceInfo = getDeviceInfo();
      await registerDevice(loggedUser.uid, deviceId, deviceInfo);
    } else {
      await updateLastLogin(loggedUser.uid, deviceId);
    }

    const profileRef = doc(db, "users", loggedUser.uid);
    const profileSnap = await getDoc(profileRef);
    const profile = profileSnap.data();

    if (onLogin) onLogin(loggedUser, profile);
    setLoading(false);
  };

  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: D.bg,
      fontFamily: F.b,
      padding: 20,
    },
    card: {
      background: D.card,
      border: `1px solid ${D.bdr}`,
      borderRadius: 16,
      padding: 40,
      width: "100%",
      maxWidth: 420,
      backdropFilter: "blur(20px)",
    },
    title: {
      fontFamily: F.dm,
      color: D.tx,
      fontSize: 24,
      marginBottom: 8,
      textAlign: "center",
    },
    subtitle: {
      color: D.txM,
      fontSize: 14,
      textAlign: "center",
      marginBottom: 28,
    },
    label: {
      color: D.txM,
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 1,
      marginBottom: 6,
      display: "block",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      background: "rgba(255,255,255,0.05)",
      border: `1px solid ${D.bdr}`,
      borderRadius: 8,
      color: D.tx,
      fontSize: 15,
      fontFamily: F.b,
      marginBottom: 18,
      outline: "none",
      boxSizing: "border-box",
    },
    button: {
      width: "100%",
      padding: "13px 0",
      background: D.ac,
      color: "#0f172a",
      border: "none",
      borderRadius: 8,
      fontSize: 15,
      fontWeight: 700,
      fontFamily: F.b,
      cursor: "pointer",
      marginTop: 4,
      opacity: (loading || isLockedOut) ? 0.6 : 1,
    },
    error: {
      background: "rgba(239,68,68,0.12)",
      border: "1px solid rgba(239,68,68,0.3)",
      color: "#fca5a5",
      padding: "10px 14px",
      borderRadius: 8,
      fontSize: 13,
      marginBottom: 16,
    },
    info: {
      background: "rgba(45,212,191,0.12)",
      border: `1px solid rgba(45,212,191,0.3)`,
      color: D.ac,
      padding: "10px 14px",
      borderRadius: 8,
      fontSize: 13,
      marginBottom: 16,
    },
    link: {
      color: D.ac,
      fontSize: 13,
      cursor: "pointer",
      textAlign: "center",
      marginTop: 16,
      background: "none",
      border: "none",
      fontFamily: F.b,
      textDecoration: "underline",
    },
    demoBox: {
      marginTop: 24,
      padding: "14px 16px",
      background: "rgba(45,212,191,0.06)",
      border: `1px solid ${D.bdr}`,
      borderRadius: 8,
    },
    demoTitle: {
      color: D.txM,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 1,
      marginBottom: 8,
    },
    demoLine: {
      color: D.txM,
      fontSize: 12,
      fontFamily: F.m,
      marginBottom: 2,
    },
    totpInput: {
      width: "100%",
      padding: "14px",
      background: "rgba(255,255,255,0.05)",
      border: `1px solid ${D.bdr}`,
      borderRadius: 8,
      color: D.tx,
      fontSize: 24,
      fontFamily: F.m,
      textAlign: "center",
      letterSpacing: 12,
      marginBottom: 18,
      outline: "none",
      boxSizing: "border-box",
    },
  };

  // --- Pantalla de codigo TOTP ---
  if (showTOTP) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Verificacion TOTP</h1>
          <p style={styles.subtitle}>
            Ingrese el codigo de 6 digitos de Google Authenticator
          </p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleTOTPSubmit}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              style={styles.totpInput}
              autoFocus
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? "Verificando..." : "Verificar"}
            </button>
          </form>

          <button
            style={styles.link}
            onClick={() => {
              setShowTOTP(false);
              setTotpCode("");
              setError("");
            }}
          >
            Volver al inicio de sesion
          </button>
        </div>
      </div>
    );
  }

  // --- Pantalla principal de login ---
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Linfedema App</h1>
        <p style={styles.subtitle}>
          Plataforma clinica Dr. David Duque
        </p>

        {error && <div style={styles.error}>{error}</div>}
        {info && <div style={styles.info}>{info}</div>}
        {isLockedOut && (
          <div style={{ ...styles.error, background: "rgba(251,146,60,0.12)", border: "1px solid rgba(251,146,60,0.3)", color: "#fdba74" }}>
            Demasiados intentos fallidos. Intente nuevamente en {lockoutSeconds} segundos.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Correo electronico</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="correo@ejemplo.com"
            style={styles.input}
            required
          />

          <label style={styles.label}>Contrasena</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Su contrasena"
            style={styles.input}
            required
          />

          <button type="submit" style={{ ...styles.button, opacity: (loading || isLockedOut) ? 0.6 : 1 }} disabled={loading || isLockedOut}>
            {isLockedOut
              ? `Bloqueado (${lockoutSeconds}s)`
              : loading
              ? "Iniciando sesion..."
              : "Iniciar Sesion"}
          </button>
        </form>

        {(onRegisterPatient || onRegisterDoctor) && (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 8 }}>
            {onRegisterPatient && (
              <button onClick={onRegisterPatient}
                style={{ width: "100%", padding: "12px 0", background: "rgba(45,212,191,0.12)", border: `1px solid rgba(45,212,191,0.3)`, borderRadius: 8, color: D.ac, fontSize: 14, fontWeight: 700, fontFamily: F.b, cursor: "pointer" }}>
                Crear cuenta — Paciente
              </button>
            )}
            {onRegisterDoctor && (
              <button onClick={onRegisterDoctor}
                style={{ width: "100%", padding: "10px 0", background: "transparent", border: `1px solid ${D.bdr}`, borderRadius: 8, color: D.txM, fontSize: 13, fontFamily: F.b, cursor: "pointer" }}>
                Solicitar acceso — Medico
              </button>
            )}
          </div>
        )}

        {onGoToRegister && !onRegisterPatient && (
          <button style={styles.link} onClick={onGoToRegister}>
            No tiene cuenta? Registrarse
          </button>
        )}

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <p style={{ color: "rgba(255,255,255,0.15)", fontSize: 10, margin: 0 }}>Plataforma clinica — Dr. David Duque · v5.0</p>
        </div>
      </div>
    </div>
  );
}
